/**
 * WalletPage - Gestion du compte prépayé client
 * Affichage solde + Recharge + Historique transactions
 */

import { useState, useEffect } from 'react';
import { useTitrePage } from '../../hooks/useTitrePage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomer } from '../../contexts/CustomerContext';
import { ecommerceApi } from '../../api/ecommerce.api';
import type { Wallet, WalletTransaction } from '../../types/ecommerce.types';

/**
 * Référence lisible pour le client. Les identifiants techniques (session
 * Stripe, UUID de commande) ne lui disent rien et, insécables, cassaient la
 * mise en page sur téléphone : on les remplace par un libellé ou on les tait.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function formatReference(reference?: string | null): string | null {
  if (!reference) return null;
  const ref = reference.trim();
  if (/^(stripe_)?cs_(live|test)_/i.test(ref) || /^pi_/i.test(ref)) return 'Paiement par carte / Bancontact';
  if (/^stripe-refund/i.test(ref)) return 'Remboursement sur votre carte';
  const sale = ref.match(/^Sale\s+(SALE-[\w-]+)$/i);
  if (sale) return `Ticket ${sale[1]}`;
  if (UUID_RE.test(ref)) return null;
  if (/^correction-/i.test(ref)) return 'Correction';
  return `Réf : ${ref}`;
}

export default function WalletPage() {
  useTitrePage('Mes FOLIES');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { customer } = useCustomer();

  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'deposit' | 'purchase' | 'refund'>('all');
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('50');
  const [depositSuccess, setDepositSuccess] = useState(false);

  // Bon cadeau
  const [codeBon, setCodeBon] = useState('');
  const [bonEnCours, setBonEnCours] = useState(false);
  const [bonErreur, setBonErreur] = useState<string | null>(null);
  const [bonSucces, setBonSucces] = useState<string | null>(null);

  useEffect(() => {
    if (!customer) {
      navigate('/shop/login');
      return;
    }

    if (searchParams.get('deposit') === 'success') {
      setDepositSuccess(true);
      searchParams.delete('deposit');
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });
      setTimeout(() => setDepositSuccess(false), 6000);
    }

    loadWalletData();
  }, [customer]);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      const [walletData, transactionsResponse] = await Promise.all([
        ecommerceApi.wallet.getBalance(),
        ecommerceApi.wallet.getTransactions({ limit: 50 }),
      ]);
      setWallet(walletData);
      // L'API retourne { data: [...], meta: {...} }
      setTransactions(transactionsResponse.data || transactionsResponse || []);
    } catch (err) {
      console.error('Erreur chargement wallet:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Le serveur est seul juge : il verrouille le bon, le marque utilisé et crédite
   * les FOLIES dans la même transaction. La page ne fait que transmettre le code
   * et afficher la phrase renvoyée — un bon déjà utilisé, expiré ou inconnu a son
   * propre message, et aucun n'est deviné ici.
   */
  const handleBonCadeau = async () => {
    const code = codeBon.trim();
    if (!code) return;

    setBonEnCours(true);
    setBonErreur(null);
    setBonSucces(null);
    try {
      const res = await ecommerceApi.giftVouchers.consommer(code);
      setBonSucces(`${res.amount_euros} € ajoutés à vos FOLIES.`);
      setCodeBon('');
      await loadWalletData();
    } catch (err: any) {
      setBonErreur(
        err?.response?.data?.message ||
          "Ce bon n'a pas pu être utilisé. Vérifiez le code et réessayez.",
      );
    } finally {
      setBonEnCours(false);
    }
  };

  const handleDeposit = async () => {
    const amountCents = Math.round(parseFloat(depositAmount) * 100);

    if (isNaN(amountCents) || amountCents < 500) {
      alert('Montant minimum : 5€');
      return;
    }

    try {
      setLoading(true);
      // Create Stripe Checkout session via backend
      const response = await ecommerceApi.stripe.createCheckoutSession({
        amount_cents: amountCents,
      });

      // Redirect to Stripe Checkout
      if (response.url) {
        window.location.href = response.url;
      } else {
        alert('Erreur: pas de lien de paiement');
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erreur lors de la recharge');
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (filter === 'all') return true;
    if (filter === 'deposit') return t.type === 'deposit';
    if (filter === 'purchase') return t.type === 'purchase';
    if (filter === 'refund') return t.type === 'refund';
    return true;
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return '💰';
      case 'purchase':
        return '🛒';
      case 'refund':
        return '↩️';
      default:
        return '💳';
    }
  };

  const getTransactionLabel = (type: string) => {
    switch (type) {
      case 'deposit':
        return 'Recharge';
      case 'purchase':
        return 'Achat';
      case 'refund':
        return 'Remboursement';
      case 'withdrawal':
        return 'Retrait';
      default:
        return type;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <button
            onClick={() => navigate('/shop')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à la boutique
          </button>

          <h1 className="text-4xl font-bold text-gray-900">Mes FOLIES</h1>
          <p className="text-gray-600 mt-1">Votre compte prépayé — 1 FOLIE = 1 €</p>
        </div>

        {depositSuccess && (
          <div className="bg-green-100 border-2 border-green-400 rounded-xl p-4 mb-6 flex items-center gap-3">
            <span className="text-2xl">✅</span>
            <div>
              <div className="font-bold text-green-800">Recharge effectuée avec succès !</div>
              <div className="text-sm text-green-700">Votre solde a été mis à jour.</div>
            </div>
          </div>
        )}

        {loading && !wallet ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto mb-4" />
            <p className="text-gray-600 text-xl">Chargement...</p>
          </div>
        ) : (
          <>
            {/* Carte Solde */}
            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl shadow-lg p-8 mb-6 text-white">
              <div className="text-lg opacity-90 mb-2">Mes FOLIES disponibles</div>
              <div className="text-6xl font-bold mb-6">
                {/* Utiliser le solde de la dernière transaction si disponible (plus fiable) */}
                {transactions.length > 0
                  ? (transactions[0].balance_after_cents / 100).toFixed(2)
                  : wallet
                    ? (wallet.balance_cents / 100).toFixed(2)
                    : '0.00'} €
              </div>
              <button
                onClick={() => setShowDepositModal(true)}
                className="bg-white text-green-700 px-8 py-3 rounded-xl font-bold text-xl hover:bg-green-50 transition-all active:scale-95"
              >
                + Recharger mes FOLIES
              </button>
            </div>

            {/* Bon cadeau */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">J'ai un bon cadeau</h2>
              <p className="text-gray-600 mb-4">
                Saisissez le code figurant sur votre bon : son montant est ajouté à vos FOLIES.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={codeBon}
                  onChange={(e) => setCodeBon(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleBonCadeau();
                  }}
                  placeholder="FOLIE-XXXX-XXXX-XXXX"
                  /* autoCapitalize : le code est en majuscules, et le clavier iOS
                     propose des minuscules par défaut. Le serveur normalise de
                     toute façon — ceci évite juste au client de se demander. */
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  disabled={bonEnCours}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl font-mono tracking-wider uppercase focus:border-green-600 focus:outline-none disabled:bg-gray-100"
                />
                <button
                  onClick={handleBonCadeau}
                  disabled={bonEnCours || !codeBon.trim()}
                  className="bg-green-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-green-700 transition-all active:scale-95 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {bonEnCours ? 'Vérification…' : 'Utiliser'}
                </button>
              </div>

              {bonSucces && (
                <div className="mt-4 bg-green-50 border-2 border-green-300 rounded-xl p-4 flex items-center gap-3">
                  <span className="text-2xl">🎁</span>
                  <div className="font-bold text-green-800">{bonSucces}</div>
                </div>
              )}
              {bonErreur && (
                <div className="mt-4 bg-red-50 border-2 border-red-300 rounded-xl p-4 text-red-800">
                  {bonErreur}
                </div>
              )}
            </div>

            {/* Filtres */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Historique</h2>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-6 py-3 rounded-lg font-bold transition-all ${
                    filter === 'all'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Toutes ({transactions.length})
                </button>
                <button
                  onClick={() => setFilter('deposit')}
                  className={`px-6 py-3 rounded-lg font-bold transition-all ${
                    filter === 'deposit'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  💰 Dépôts
                </button>
                <button
                  onClick={() => setFilter('purchase')}
                  className={`px-6 py-3 rounded-lg font-bold transition-all ${
                    filter === 'purchase'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  🛒 Achats
                </button>
                <button
                  onClick={() => setFilter('refund')}
                  className={`px-6 py-3 rounded-lg font-bold transition-all ${
                    filter === 'refund'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  ↩️ Remboursements ({transactions.filter(t => t.type === 'refund').length})
                </button>
              </div>
            </div>

            {/* Liste Transactions */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              {filteredTransactions.length === 0 ? (
                <div className="text-center py-12 text-gray-600">
                  <div className="text-6xl mb-4">💳</div>
                  <p className="text-xl">Aucune transaction</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTransactions.map(transaction => {
                    const isRefund = transaction.type === 'refund';
                    const refundMetadata = isRefund && transaction.metadata ? transaction.metadata : null;

                    return (
                      <div
                        key={transaction.id}
                        className={`p-4 border-2 rounded-lg transition-colors ${
                          isRefund
                            ? 'border-green-300 bg-green-50 hover:border-green-400'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {/* min-w-0 / shrink-0 : une référence longue (identifiant
                            Stripe) poussait la colonne des montants hors de la
                            carte sur téléphone (capture du 30/08/2026). */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                            <div className="text-3xl sm:text-4xl shrink-0">{getTransactionIcon(transaction.type)}</div>
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-lg">
                                {getTransactionLabel(transaction.type)}
                              </div>
                              <div className="text-sm text-gray-600">
                                {formatDate(transaction.created_at)}
                              </div>
                              {formatReference(transaction.reference) && (
                                <div className="text-xs text-gray-600 mt-1 break-all">
                                  {formatReference(transaction.reference)}
                                </div>
                              )}

                              {/* Détails du remboursement */}
                              {isRefund && refundMetadata && (
                                <div className="mt-2 space-y-1 text-sm">
                                  {refundMetadata.refund_number && (
                                    <div className="text-gray-700">
                                      <span className="font-medium">N° remboursement:</span> {refundMetadata.refund_number}
                                    </div>
                                  )}
                                  {refundMetadata.refund_type && (
                                    <div className="text-gray-700">
                                      <span className="font-medium">Type:</span>{' '}
                                      {refundMetadata.refund_type === 'product_missing' && 'Produit manquant'}
                                      {refundMetadata.refund_type === 'product_defective' && 'Produit défectueux'}
                                      {refundMetadata.refund_type === 'weight_difference' && 'Différence de poids'}
                                      {refundMetadata.refund_type === 'order_cancelled' && 'Commande annulée'}
                                      {refundMetadata.refund_type === 'goodwill_gesture' && 'Geste commercial'}
                                    </div>
                                  )}
                                  {refundMetadata.reason && (
                                    <div className="text-gray-700">
                                      <span className="font-medium">Motif:</span> {refundMetadata.reason}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0 whitespace-nowrap">
                            <div
                              className={`text-xl sm:text-2xl font-bold ${
                                // deposit et refund = positif (vert), debit et purchase = négatif (rouge)
                                (transaction.type === 'deposit' || transaction.type === 'refund')
                                  ? 'text-green-700'
                                  : 'text-red-600'
                              }`}
                            >
                              {/* deposit et refund = +, debit et purchase = - */}
                              {(transaction.type === 'deposit' || transaction.type === 'refund') ? '+' : '-'}
                              {(Math.abs(transaction.amount_cents) / 100).toFixed(2)} €
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              Solde: {(transaction.balance_after_cents / 100).toFixed(2)} €
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal Recharge */}
      {showDepositModal && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
          onClick={() => setShowDepositModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Recharger mes FOLIES</h2>

            <div className="mb-6">
              <label className="block text-lg font-bold text-gray-900 mb-3">Montant à créditer</label>

              {/* Montants prédéfinis */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {['20', '50', '100'].map(amount => (
                  <button
                    key={amount}
                    onClick={() => setDepositAmount(amount)}
                    className={`py-3 rounded-lg font-bold text-xl transition-all ${
                      depositAmount === amount
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {amount} €
                  </button>
                ))}
              </div>

              {/* Montant personnalisé */}
              <div className="relative">
                <input
                  type="number"
                  min="5"
                  step="5"
                  value={depositAmount}
                  onChange={e => setDepositAmount(e.target.value)}
                  className="w-full px-4 py-4 text-2xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500 text-center"
                  placeholder="Montant personnalisé"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-400">
                  €
                </span>
              </div>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
              <div className="text-sm text-gray-700">
                <div className="font-bold mb-1">💳 Paiement sécurisé</div>
                <div>Vous serez redirigé vers notre plateforme de paiement sécurisée.</div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDepositModal(false)}
                className="flex-1 py-3 border-2 border-gray-300 rounded-lg font-bold hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDeposit}
                disabled={loading}
                className="flex-1 bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 disabled:bg-gray-400 transition-colors"
              >
                {loading ? 'Traitement...' : 'Recharger'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
